import React, { useRef, useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { PenLine, Eraser, Trash2, X } from 'lucide-react';

/**
 * ScratchPad Component
 * A slide-out drawing canvas for solving math problems
 */
const ScratchPad = ({ isOpen, onClose }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [tool, setTool] = useState('pen'); // 'pen' or 'eraser'
  const [context, setContext] = useState(null);

  // Initialize canvas
  useEffect(() => {
    if (canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      
      // Set canvas size
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      
      // Set drawing styles
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = '#1a1a2e';
      ctx.lineWidth = 3;
      
      setContext(ctx);
    }
  }, [isOpen]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (canvasRef.current && context) {
        const canvas = canvasRef.current;
        const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
        canvas.width = canvas.offsetWidth;
        canvas.height = canvas.offsetHeight;
        context.putImageData(imageData, 0, 0);
        context.lineCap = 'round';
        context.lineJoin = 'round';
        context.strokeStyle = tool === 'eraser' ? '#ffffff' : '#1a1a2e';
        context.lineWidth = tool === 'eraser' ? 20 : 3;
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [context, tool]);

  const startDrawing = (e) => {
    if (!context) return;
    
    const { offsetX, offsetY } = getCoordinates(e);
    context.beginPath();
    context.moveTo(offsetX, offsetY);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing || !context) return;
    
    const { offsetX, offsetY } = getCoordinates(e);
    context.lineTo(offsetX, offsetY);
    context.stroke();
  };

  const stopDrawing = () => {
    if (!context) return;
    context.closePath();
    setIsDrawing(false);
  };

  const getCoordinates = (e) => {
    if (e.touches) {
      const rect = canvasRef.current.getBoundingClientRect();
      return {
        offsetX: e.touches[0].clientX - rect.left,
        offsetY: e.touches[0].clientY - rect.top
      };
    }
    return { offsetX: e.nativeEvent.offsetX, offsetY: e.nativeEvent.offsetY };
  };

  const selectTool = (selectedTool) => {
    setTool(selectedTool);
    if (context) {
      if (selectedTool === 'eraser') {
        context.strokeStyle = '#ffffff';
        context.lineWidth = 20;
      } else {
        context.strokeStyle = '#1a1a2e';
        context.lineWidth = 3;
      }
    }
  };

  const clearCanvas = () => {
    if (canvasRef.current && context) {
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    }
  };

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/30 z-40 transition-opacity"
          onClick={onClose}
        />
      )}
      
      {/* Slide-out Panel */}
      <div 
        className={`fixed top-0 right-0 h-full w-80 md:w-96 bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        data-testid="scratch-pad-panel"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-4 flex items-center justify-between">
          <h3 className="font-bold text-lg">Scratch Pad</h3>
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={onClose}
            className="text-white hover:bg-white/20"
            data-testid="close-scratch-pad"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>
        
        {/* Toolbar */}
        <div className="flex items-center justify-center gap-2 p-3 border-b bg-gray-50">
          <Button
            variant={tool === 'pen' ? 'default' : 'outline'}
            size="sm"
            onClick={() => selectTool('pen')}
            className={tool === 'pen' ? 'bg-purple-600 hover:bg-purple-700' : ''}
            data-testid="pen-tool"
          >
            <PenLine className="h-4 w-4 mr-1" />
            Pen
          </Button>
          <Button
            variant={tool === 'eraser' ? 'default' : 'outline'}
            size="sm"
            onClick={() => selectTool('eraser')}
            className={tool === 'eraser' ? 'bg-purple-600 hover:bg-purple-700' : ''}
            data-testid="eraser-tool"
          >
            <Eraser className="h-4 w-4 mr-1" />
            Eraser
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={clearCanvas}
            className="text-red-600 border-red-300 hover:bg-red-50"
            data-testid="clear-canvas"
          >
            <Trash2 className="h-4 w-4 mr-1" />
            Clear
          </Button>
        </div>
        
        {/* Canvas */}
        <div className="flex-1 h-[calc(100%-120px)] p-2">
          <canvas
            ref={canvasRef}
            className="w-full h-full bg-white border-2 border-dashed border-gray-300 rounded-lg cursor-crosshair touch-none"
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={stopDrawing}
            data-testid="scratch-pad-canvas"
          />
        </div>
        
        {/* Footer hint */}
        <div className="p-3 text-center text-xs text-gray-500 border-t">
          Use this space to work out your calculations
        </div>
      </div>
    </>
  );
};

export default ScratchPad;
